import { useState } from 'react';
import { Upload, X, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

type ImportExpensesProps = {
  carId: string;
  onClose: () => void;
  onImportComplete: () => void;
};

type ExpenseToInsert = {
  car_id: string;
  user_id: string;
  date: string;
  type: string;
  title: string;
  amount: number;
  mileage?: number;
  fuel_liters?: number;
  fuel_type?: string | null;
  description: string;
  image_url: string;
  ai_extracted: Record<string, any>;
  log_entry_id?: string;
};

type FuelData = {
  price: number;
  liters: number;
};

export function ImportExpenses({ carId, onClose, onImportComplete }: ImportExpensesProps) {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [dragging, setDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
      setFile(droppedFile);
      setError(null);
      setSuccess(false);
    } else {
      setError('Please choose a .xlsx or .xls file');
    }
  };

  const parseExcelFile = async (file: File, carFuelType: string | null): Promise<ExpenseToInsert[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const expensesToInsert: ExpenseToInsert[] = [];
          const fuelDataByDate: Map<string, FuelData> = new Map();

          workbook.SheetNames.forEach((sheetName) => {
            const sheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet);

            console.log(`[IMPORT] Sheet: ${sheetName}, found ${jsonData.length} raw rows`);

            // Helper to find a key in the row object regardless of exact whitespace/newlines
            const getNormalizedRowData = (row: any, target: string) => {
              const normalizedTarget = target.toLowerCase().replace(/\s+/g, '');
              const key = Object.keys(row).find(k => k.toLowerCase().replace(/\s+/g, '') === normalizedTarget);
              return key ? row[key] : undefined;
            };

            jsonData.forEach((row: any, rowIndex: number) => {
              // Usually the first few rows might be headers or empty, let's be more flexible
              let dateValue = row['__EMPTY'] || getNormalizedRowData(row, 'WHEN') || row['WHEN'];
              if (!dateValue || dateValue === 'WHEN') return;

              const monthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
                'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
              if (typeof dateValue === 'string' && monthNames.includes(dateValue.toUpperCase())) {
                return;
              }

              let parsedDate: string | null = null;

              if (typeof dateValue === 'number') {
                // Excel date
                parsedDate = new Date(Math.round((dateValue - 25569) * 86400 * 1000)).toISOString().split('T')[0];
              } else if (typeof dateValue === 'string') {
                const isOldFormat = sheetName === '2018' || sheetName === '2019';
                const parts = dateValue.split('.');

                if (parts.length === 3) {
                  let day: number, month: number, year: number;

                  if (isOldFormat) {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                    day = parseInt(parts[2], 10);
                  } else {
                    day = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                    year = parseInt(parts[2], 10);
                  }

                  const parsed = new Date(year, month, day);
                  if (!isNaN(parsed.getTime())) {
                    parsedDate = parsed.toISOString().split('T')[0];
                  }
                } else {
                  const parsed = new Date(dateValue);
                  if (!isNaN(parsed.getTime())) {
                    parsedDate = parsed.toISOString().split('T')[0];
                  }
                }
              }

              if (!parsedDate) {
                console.log(`[IMPORT] Skipping row ${rowIndex} in ${sheetName}: Invalid date format (${dateValue})`);
              }

              const date = parsedDate;
              if (!date) return;

              const what = row['__EMPTY_1'] || getNormalizedRowData(row, 'WHAT') || row['WHAT'] || '';
              const comment = row['__EMPTY_2'] || getNormalizedRowData(row, 'DESCRIPTION') || row['DESCRIPTION'] || '';
              let mergedDescription = what;
              if (comment) {
                  mergedDescription = what ? `${what} - ${comment}` : comment;
              }

              const categoryColumns: Record<string, string> = {
                'Riepas': 'tyres',
                'Remonts': 'repairs',
                'CSDD/Polise': 'taxes',
                'CSDD/Polise/Kasko': 'taxes',
                'Stāvlaukums': 'parking',
                'Degviela(Cena)': 'fuel_price_temp',
                'Degviela(Litri)': 'fuel_liters_temp',
                'Automazgāšana/Piederumi': 'car_wash',
                'Mazgāšana': 'car_wash',
                'Automazgāšana': 'car_wash',
              };

              let foundInCategory = false;
              Object.entries(categoryColumns).forEach(([columnName, type]) => {
                const amount = getNormalizedRowData(row, columnName);
                
                if (amount !== undefined && amount !== null && typeof amount === 'number' && amount > 0) {
                  foundInCategory = true;
                  try {
                    const dateObj = new Date(date);
                    const dateKey = `${date}_${mergedDescription || 'fuel'}`;

                    if (type === 'fuel_price_temp') {
                      if (!fuelDataByDate.has(dateKey)) {
                        fuelDataByDate.set(dateKey, { price: 0, liters: 0 });
                      }
                      fuelDataByDate.get(dateKey)!.price = parseFloat(amount.toString());
                    } else if (type === 'fuel_liters_temp') {
                      if (!fuelDataByDate.has(dateKey)) {
                        fuelDataByDate.set(dateKey, { price: 0, liters: 0 });
                      }
                      fuelDataByDate.get(dateKey)!.liters = parseFloat(amount.toString());
                    } else {
                      expensesToInsert.push({
                        car_id: carId,
                        user_id: user?.id || '',
                        date: dateObj.toISOString(),
                        type,
                        title: '',
                        amount: parseFloat(amount.toString()),
                        mileage: 0,
                        description: mergedDescription,
                        image_url: '',
                        ai_extracted: {},
                      });
                    }
                  } catch (err) {
                    console.log(`[IMPORT] Error processing category ${columnName} at row ${rowIndex}:`, err);
                  }
                }
              });

              if (!foundInCategory && !fuelDataByDate.has(`${date}_${mergedDescription || 'fuel'}`)) {
                // Check if there was any data in this row at all
                const hasAnyData = Object.values(row).some(v => v !== undefined && v !== null && v !== '');
                if (hasAnyData) {
                  // console.log(`[IMPORT] Row ${rowIndex} in ${sheetName} has date but no positive amounts in categories.`);
                }
              }
            });
          });

          fuelDataByDate.forEach((fuelData, dateKey) => {
            const date = dateKey.substring(0, 10);
            const whatDesc = dateKey.substring(11);
            if (fuelData.price > 0 && fuelData.liters > 0) {
              const dateObj = new Date(date);

              const expense: ExpenseToInsert = {
                car_id: carId,
                user_id: user?.id || '',
                date: dateObj.toISOString(),
                type: 'fuel',
                title: '',
                amount: fuelData.price,
                fuel_liters: fuelData.liters,
                fuel_type: carFuelType,
                mileage: 0,
                description: whatDesc === 'fuel' ? '' : whatDesc,
                image_url: '',
                ai_extracted: {},
              };
              expensesToInsert.push(expense);
            }
          });

          resolve(expensesToInsert);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsBinaryString(file);
    });
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please choose a file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: carData } = await supabase.from('cars').select('fuel_type').eq('id', carId).single();
      const carFuelType = carData?.fuel_type || null;

      const parsedExpenses = await parseExcelFile(file, carFuelType);

      if (parsedExpenses.length === 0) {
        setError('No importable data found in file');
        setLoading(false);
        return;
      }

      console.log(`[IMPORT] Attempting to import ${parsedExpenses.length} records...`);

      const logEntriesToInsert = parsedExpenses.map(e => ({
        car_id: carId,
        user_id: user?.id || '',
        date: e.date,
        mileage: e.mileage || 0,
        title: e.title,
      }));

      const { data: insertedLogs, error: logError } = await supabase
        .from('log_entries')
        .insert(logEntriesToInsert)
        .select('id');

      if (logError) {
        console.error('[IMPORT] LogEntry insert error:', logError);
        throw logError;
      }

      if (!insertedLogs || insertedLogs.length === 0) {
        throw new Error('Failed to create log entries');
      }

      // 2. Map expenses to their new log_entry_ids and strip mileage
      const expensesWithLogs = parsedExpenses.map((expense, index) => {
        const { mileage, ...expenseData } = expense;
        return {
          ...expenseData,
          log_entry_id: insertedLogs[index].id
        };
      });

      // 3. Insert expenses
      const { error: insertError } = await supabase
        .from('expenses')
        .insert(expensesWithLogs);

      if (insertError) {
        console.error('[IMPORT] Expense insert error:', insertError);
        throw insertError;
      }

      console.log(`[IMPORT] Successfully inserted ${expensesWithLogs.length} expenses.`);

      // Update car mileage if any imported expense has a higher mileage
      const maxImportedMileage = Math.max(...parsedExpenses.map(e => e.mileage || 0), 0);
      if (maxImportedMileage > 0) {
        const { data: carData } = await supabase
          .from('cars')
          .select('mileage')
          .eq('id', carId)
          .single();

        if (carData && (carData.mileage === null || maxImportedMileage > carData.mileage)) {
          await supabase
            .from('cars')
            .update({ mileage: maxImportedMileage })
            .eq('id', carId);
        }
      }

      setImportedCount(parsedExpenses.length);
      setSuccess(true);
      setLoading(false);

      setTimeout(() => {
        onImportComplete();
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Import error:', err);
      setError(`Error: ${err.message || 'Check the file format.'}`);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Upload size={28} className="text-blue-600" />
            Import from Excel
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="inline-flex p-4 bg-green-100 rounded-full mb-4">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Successfully imported!
            </h3>
            <p className="text-gray-600">
              Imported {importedCount} records
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Upload a .xlsx file with expenses. The file must be in the same format as the example.
              </p>

              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragging
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-500'
                  }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="inline-flex p-4 bg-blue-100 rounded-full mb-4">
                    <FileSpreadsheet size={32} className="text-blue-600" />
                  </div>
                  <p className="text-gray-700 font-semibold mb-2">
                    {file ? file.name : 'Choose Excel file'}
                  </p>
                  <p className="text-sm text-gray-500">
                    or drag it here
                  </p>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!file || loading}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
